import { useEffect, useRef } from 'react';
import { App as AntdApp } from 'antd';

import { getGovernorState, onGovernorChange } from '../../api/governor';

export const PAUSE_NOTICE_KEY = 'keepr-request-pause';
// The server asked us to wait (a 429 that named its wait), and the budget it
// spent was the network address's: the WAF's, or a signed-out visitor's.
export const PAUSE_NOTICE_TEXT = 'keepr is getting too many requests from this network. Pausing for a moment.';
// The same, when the budget was the signed-in account's: keepr-api counts a
// session's reads per person, across every tab and device (KPR-989).
export const ACCOUNT_NOTICE_TEXT = 'keepr is getting a lot of requests from your account. Pausing for a moment.';
// Requests got no answer at all (a run of opaque network failures).
export const NETWORK_NOTICE_TEXT = 'Having trouble reaching keepr. Trying again in a moment.';

export const noticeTextFor = (reason, scope) => {
	if (reason === 'network') return NETWORK_NOTICE_TEXT;
	return scope === 'account' ? ACCOUNT_NOTICE_TEXT : PAUSE_NOTICE_TEXT;
};

/**
 * The ONE notice the app shows while the request governor
 * (src/api/governor.js) holds new reads: a warning message under a fixed
 * key, so a pause that is extended, or whose reason changes, updates it in
 * place and it never stacks. Its words follow the reason: too many requests
 * for a rate-limited pause — from your account or from this network, as the
 * governor's `scope` says — and trouble reaching keepr for a network one. It lasts
 * as long as the pause, and closes when the pause ends — early, when a
 * network pause ends on a readable answer. Mounted once, in AppRoot, so every
 * route has it and no surface needs a toast of its own for "wait".
 */
export default function RequestPauseNotice() {
	const { message } = AntdApp.useApp();
	const showing = useRef(false);

	useEffect(() => {
		const show = state => {
			if (state.paused) {
				const seconds = Math.max(1, Math.ceil((state.pausedUntil - Date.now()) / 1000));
				message?.warning?.({ key: PAUSE_NOTICE_KEY, content: noticeTextFor(state.reason, state.scope), duration: seconds });
				showing.current = true;
			} else if (showing.current) {
				message?.destroy?.(PAUSE_NOTICE_KEY);
				showing.current = false;
			}
		};
		show(getGovernorState());
		return onGovernorChange(show);
	}, [message]);

	return null;
}
